import saveAs from "file-saver";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
export interface Option {
  label: string;
  value: string;
}

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export interface InvoiceTemplateType {
  id: string;
  name: string;
  address: string;
  billingEmail: string;
  invoiceReference: string;
  invoiceTitle: string;
  bankDetails: string;
  bankDetailsPosition: BankDetailsPosition;
  emailTemplate: string;
  signatureTemplate: string;
  emailAddress: string;
  emailSender: string;
  emailPassword: string;
  outgoingServer: string;
  port: string;
  createdDate: Date;
  updatedBy?: string;
  createdBy: string;
  existingTemplate: string;
  mccmnc: string;
  historyTimestamp: Date;
}

export interface InvoiceTemplateHistory {
  id: string;
  snapshot: InvoiceTemplateType & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
}
export enum BankDetailsPosition {
  UP,
  DOWN,
}

export const exportInvoiceTempalte = async (
  params: Params<InvoiceTemplateType> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/templates/invoices/csv", {
      params,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "InvoiceTempalte.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching InvoiceTempalte:",
      error.response?.data?.message || error.message || error
    );
  }
};

export async function fetchInvoiceTemplates(
  params: Params<InvoiceTemplateType>
): Promise<IPageable<InvoiceTemplateType>> {
  try {
    const response = await axiosInstance.get("/templates/invoices", { params });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the invoiceTemplates."
    );
  }
}

export async function fetchInvoiceTemplate(
  id: string
): Promise<InvoiceTemplateType> {
  try {
    const response = await axiosInstance.get("/templates/invoices/" + id);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the invoiceTemplate."
    );
  }
}

export const createInvoiceTemplate = async (
  invoiceTemplateData: Partial<InvoiceTemplateType>
) => {
  try {
    const response = await axiosInstance.post(
      "/templates/invoices",
      invoiceTemplateData
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while Adding the invoiceTemplate."
    );
  }
};

export const updateInvoiceTemplate = async (
  invoiceTemplateData: Partial<InvoiceTemplateType>
) => {
  try {
    const response = await axiosInstance.put(
      `/templates/invoices/${invoiceTemplateData.id}`,
      invoiceTemplateData
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the invoiceTemplate."
    );
  }
};

export const useInvoiceTemplateQuery = (
  params: Params<InvoiceTemplateType>
): UseQueryResult<IPageable<InvoiceTemplateType>, Error> => {
    const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["invoiceTemplate", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchInvoiceTemplates(effectiveParams),
  });
};

export async function fetchInvoiceTemplateHistory(
  id: InvoiceTemplateType["id"],
  params: Params<InvoiceTemplateHistory> = {}
): Promise<IPageable<InvoiceTemplateType & { historyTimestamp: Date }>> {
  try {
    const response = await axiosInstance.get(`/templates/invoices/history`, { params: { mainId: id, sort: "historyTimestamp,desc", ...params } });
    const data = response.data;
   return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the Invoice Template history for ID ${id}.`
    );
  }
}
